/* SHENXING storefront behaviour: reveal-on-scroll, sticky buy bar, AJAX add-to-cart.
   Everything degrades: without JS the reveal targets are visible and the forms POST natively. */
(() => {
  'use strict';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Reveal sections as they enter the viewport. */
  function initReveal(root = document) {
    const targets = root.querySelectorAll('.sx-rise:not([data-sx-seen])');
    if (!targets.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      targets.forEach((el) => { el.dataset.sxSeen = '1'; el.classList.add('is-in'); });
      return;
    }
    const io = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    targets.forEach((el) => { el.dataset.sxSeen = '1'; io.observe(el); });
  }

  /** Show the sticky buy bar once the hero CTA has scrolled away. */
  function initStickyBar() {
    const bar = document.querySelector('[data-sx-bar]');
    const sentinel = document.querySelector('[data-sx-bar-after]');
    if (!bar || !sentinel || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-in', entry.boundingClientRect.top < 0 && !entry.isIntersecting);
    }, { threshold: 0 });
    io.observe(sentinel);
  }

  /**
   * Add to cart without a page reload, then send the buyer to checkout.
   * A failed request falls back to the browser's own form submission.
   */
  function initAddToCart() {
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-sx-add]')) return;
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      const label = button ? button.textContent : '';
      if (button) { button.disabled = true; button.textContent = 'Sealing…'; }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(form.elements.id.value), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error(`cart/add returned ${res.status}`);
        window.location.assign(form.dataset.sxAdd || '/cart');
      } catch (err) {
        console.error('[shenxing] add to cart failed, falling back to form post', err);
        if (button) { button.disabled = false; button.textContent = label; }
        form.submit();
      }
    });
  }

  function boot() {
    initReveal();
    initStickyBar();
    initAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor re-renders sections in place; re-arm the reveal for new nodes.
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
})();

/* Reviews: photo lightbox + progressive disclosure of the review cards. */
(() => {
  const ICONS = {
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>'
  };

  let box = null;
  let shots = [];
  let at = 0;
  let opener = null;

  function build() {
    if (box) return box;
    box = document.createElement('dialog');
    box.className = 'sx-lb';
    box.setAttribute('aria-label', 'Customer photos');
    box.innerHTML =
      '<button type="button" class="sx-lb__x" data-lb="close" aria-label="Close">' + ICONS.close + '</button>' +
      '<button type="button" class="sx-lb__nav sx-lb__nav--prev" data-lb="prev" aria-label="Previous photo">' + ICONS.prev + '</button>' +
      '<figure class="sx-lb__fig">' +
      '<img class="sx-lb__img" alt="">' +
      '<figcaption class="sx-lb__cap"></figcaption>' +
      '</figure>' +
      '<button type="button" class="sx-lb__nav sx-lb__nav--next" data-lb="next" aria-label="Next photo">' + ICONS.next + '</button>';
    document.body.appendChild(box);

    box.addEventListener('click', (event) => {
      const hit = event.target.closest('[data-lb]');
      if (!hit) {
        // A click on the dialog itself is a click on the backdrop area.
        if (event.target === box) box.close();
        return;
      }
      if (hit.dataset.lb === 'close') box.close();
      if (hit.dataset.lb === 'prev') show(at - 1);
      if (hit.dataset.lb === 'next') show(at + 1);
    });

    box.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(at - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(at + 1); }
    });

    box.addEventListener('close', () => {
      box.querySelector('.sx-lb__img').removeAttribute('src');
      if (opener && document.contains(opener)) opener.focus();
      opener = null;
    });

    return box;
  }

  function show(next) {
    if (next < 0 || next >= shots.length) return;
    at = next;
    const shot = shots[at];
    const img = box.querySelector('.sx-lb__img');
    img.src = shot.src;
    img.alt = shot.alt;
    box.querySelector('.sx-lb__cap').textContent =
      shot.alt + '  —  ' + (at + 1) + ' / ' + shots.length;
    box.querySelector('[data-lb="prev"]').disabled = at === 0;
    box.querySelector('[data-lb="next"]').disabled = at === shots.length - 1;
  }

  function openFrom(button) {
    const scope = button.closest('[data-sx-photos]');
    if (!scope) return;
    const buttons = Array.from(scope.querySelectorAll('[data-sx-full]'));
    shots = buttons.map((el) => ({ src: el.dataset.sxFull, alt: el.dataset.sxAlt || '' }));
    if (!shots.length) return;
    opener = button;
    build();
    show(Math.max(0, buttons.indexOf(button)));
    if (typeof box.showModal === 'function') box.showModal();
    else box.setAttribute('open', '');
  }

  function loadMore(button) {
    const scope = button.closest('[data-sx-reviews]');
    if (!scope) return;
    const step = parseInt(button.dataset.sxStep, 10) || 9;
    const hidden = Array.from(scope.querySelectorAll('.sx-rv__card.is-hidden'));
    hidden.slice(0, step).forEach((card) => card.classList.remove('is-hidden'));
    const left = hidden.length - step;
    if (left > 0) {
      button.textContent = button.dataset.sxLabel + ' (' + left + ')';
    } else {
      button.remove();
    }
  }

  document.addEventListener('click', (event) => {
    const photo = event.target.closest('[data-sx-full]');
    if (photo) { event.preventDefault(); openFrom(photo); return; }

    const more = event.target.closest('[data-sx-more]');
    if (more) { event.preventDefault(); loadMore(more); }
  });
})();
